import React, { useState } from 'react';
import { Settings, X } from 'lucide-react';
import { AppSettings, InsightStats, DEFAULT_INSIGHT_STATS } from '../types';

// ─── Category + metric definitions ──────────────────────────────────────────

type InsightStatKey = keyof Omit<InsightStats, 'metrics'>;

export interface InsightMetric {
  id: string;
  name: string;
  desc: string;
}

interface InsightCategory {
  id: InsightStatKey;
  label: string;
  icon: string;
  description: string;
  metrics: InsightMetric[];
}

export const INSIGHT_CATEGORIES: InsightCategory[] = [
  {
    id: 'attacking',
    label: 'Attacking',
    icon: '⚡',
    description: 'Kill percentage, hitting efficiency, and attack error tendencies.',
    metrics: [
      { id: 'kill_pct',        name: 'Kill %',              desc: 'Kills ÷ attack attempts' },
      { id: 'hit_efficiency',  name: 'Hitting Efficiency',  desc: '(Kills − Errors) ÷ attempts — net effectiveness' },
      { id: 'total_kills',     name: 'Total Kills',         desc: 'Season total successful attacks' },
      { id: 'attack_errors',   name: 'Attack Errors',       desc: 'Season total terminated-rally errors' },
      { id: 'attack_attempts', name: 'Attack Attempts',     desc: 'Season total attack attempts (context)' },
    ],
  },
  {
    id: 'serving',
    label: 'Serving',
    icon: '🏐',
    description: 'Ace rate and service error tendencies.',
    metrics: [
      { id: 'ace_rate',          name: 'Ace Rate',            desc: 'Aces ÷ serve attempts' },
      { id: 'serve_error_rate',  name: 'Service Error Rate',  desc: 'Errors ÷ serve attempts' },
      { id: 'total_aces',        name: 'Total Aces',          desc: 'Season total aces' },
      { id: 'service_errors',    name: 'Service Errors',      desc: 'Season total service errors' },
    ],
  },
  {
    id: 'defense',
    label: 'Defense',
    icon: '🛡️',
    description: 'Digging volume and consistency per set.',
    metrics: [
      { id: 'digs_per_set', name: 'Digs / Set',   desc: 'Average digs recorded per set' },
      { id: 'dig_errors',   name: 'Dig Errors',   desc: 'Season total dig errors' },
    ],
  },
  {
    id: 'blocking',
    label: 'Blocking',
    icon: '✋',
    description: 'Total blocks per set from solo and assist blocks.',
    metrics: [
      { id: 'blocks_per_set', name: 'Blocks / Set',    desc: 'Average total blocks (solo + assist) per set' },
      { id: 'block_solos',    name: 'Block Solos',     desc: 'Season total solo blocks' },
      { id: 'block_assists',  name: 'Block Assists',   desc: 'Season total block assists' },
    ],
  },
  {
    id: 'setting',
    label: 'Setting',
    icon: '🎯',
    description: 'Setting volume and distribution.',
    metrics: [
      { id: 'assists_per_set', name: 'Assists / Set', desc: 'Average setting assists per set' },
    ],
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Returns true if a metric is enabled (absent key = enabled). */
export const isMetricOn = (insightStats: InsightStats, metricId: string): boolean =>
  insightStats.metrics?.[metricId] !== false;

// ─── Component ───────────────────────────────────────────────────────────────

interface InsightStatSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  appSettings: AppSettings;
  setAppSettings: React.Dispatch<React.SetStateAction<AppSettings>>;
}

const InsightStatSettings: React.FC<InsightStatSettingsProps> = ({
  isOpen, onClose, appSettings, setAppSettings,
}) => {
  const [activeTab, setActiveTab] = useState<InsightStatKey>('attacking');

  if (!isOpen) return null;

  const insightStats: InsightStats = { ...DEFAULT_INSIGHT_STATS, ...appSettings.insightStats };

  const enabledCatCount = INSIGHT_CATEGORIES.filter(c => insightStats[c.id]).length;

  const toggleCategory = (key: InsightStatKey, value: boolean) => {
    setAppSettings(prev => ({
      ...prev,
      insightStats: { ...DEFAULT_INSIGHT_STATS, ...prev.insightStats, [key]: value },
    }));
  };

  const toggleMetric = (metricId: string, value: boolean) => {
    setAppSettings(prev => {
      const prevMetrics = prev.insightStats?.metrics ?? {};
      return {
        ...prev,
        insightStats: {
          ...DEFAULT_INSIGHT_STATS,
          ...prev.insightStats,
          metrics: { ...prevMetrics, [metricId]: value },
        },
      };
    });
  };

  const activeCat = INSIGHT_CATEGORIES.find(c => c.id === activeTab)!;
  const catEnabled = insightStats[activeTab];
  const activeMetrics = activeCat.metrics;
  const enabledMetricCount = activeMetrics.filter(m => isMetricOn(insightStats, m.id)).length;

  // Tab-level indicator: grey dot = category off; amber dot = category on but some metrics off
  const getTabIndicator = (cat: InsightCategory) => {
    if (!insightStats[cat.id]) return 'grey';
    const allOn = cat.metrics.every(m => isMetricOn(insightStats, m.id));
    return allOn ? null : 'amber';
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/50 backdrop-blur-sm p-0 md:p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-t-2xl md:rounded-2xl w-full md:max-w-lg max-h-[92vh] flex flex-col shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center">
              <Settings className="w-4 h-4 text-violet-600" />
            </div>
            <div>
              <h3 className="font-bold text-slate-900 leading-tight">AI Insight Stats</h3>
              <p className="text-xs text-slate-400">{enabledCatCount} of {INSIGHT_CATEGORIES.length} categories active</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Description */}
        <div className="px-5 py-3 bg-violet-50 border-b border-violet-100 shrink-0">
          <p className="text-xs text-violet-700">
            Disable categories or individual metrics your team doesn't reliably track.
            Disabled items are omitted entirely from the Gemini prompt.
          </p>
        </div>

        {/* Tab bar */}
        <div className="flex overflow-x-auto border-b border-slate-200 shrink-0 px-3 gap-1 py-1.5 scrollbar-hide">
          {INSIGHT_CATEGORIES.map(cat => {
            const indicator = getTabIndicator(cat);
            const active = activeTab === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => setActiveTab(cat.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-colors shrink-0
                  ${active ? 'bg-violet-100 text-violet-700' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'}`}
              >
                <span>{cat.icon}</span>
                {cat.label}
                {indicator === 'grey'  && <span className="w-1.5 h-1.5 rounded-full bg-slate-300 shrink-0" />}
                {indicator === 'amber' && <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">

          {/* Category master toggle */}
          <div className={`flex items-start justify-between gap-4 p-4 rounded-xl border transition-colors
            ${catEnabled ? 'bg-violet-50 border-violet-200' : 'bg-slate-50 border-slate-200'}`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                <span className="text-lg leading-none">{activeCat.icon}</span>
                <span className="font-bold text-slate-800">{activeCat.label}</span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full
                  ${catEnabled ? 'bg-violet-200 text-violet-800' : 'bg-slate-200 text-slate-500'}`}
                >
                  {catEnabled ? 'Category On' : 'Category Off'}
                </span>
              </div>
              <p className="text-xs text-slate-500">{activeCat.description}</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-0.5">
              <input type="checkbox" className="sr-only peer"
                checked={catEnabled}
                onChange={e => toggleCategory(activeTab, e.target.checked)}
              />
              <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-violet-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600" />
            </label>
          </div>

          {/* Per-metric toggles */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                Individual Metrics
              </p>
              {catEnabled && (
                <p className="text-[10px] text-slate-400">
                  {enabledMetricCount} / {activeMetrics.length} active
                </p>
              )}
            </div>
            <div className="space-y-2">
              {activeMetrics.map(metric => {
                const metricOn = isMetricOn(insightStats, metric.id);
                const effectivelyOn = catEnabled && metricOn;
                return (
                  <div
                    key={metric.id}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all
                      ${!catEnabled
                        ? 'border-slate-100 bg-slate-50 opacity-40 pointer-events-none'
                        : effectivelyOn
                          ? 'border-slate-200 bg-white'
                          : 'border-slate-200 bg-slate-50'
                      }`}
                  >
                    {/* Color swatch */}
                    <div className={`w-1.5 self-stretch rounded-full shrink-0 ${effectivelyOn ? 'bg-violet-400' : 'bg-slate-200'}`} />

                    {/* Text */}
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-semibold ${effectivelyOn ? 'text-slate-800' : 'text-slate-400'}`}>
                        {metric.name}
                      </div>
                      <div className="text-xs text-slate-400 truncate">{metric.desc}</div>
                    </div>

                    {/* Toggle */}
                    <label className="relative inline-flex items-center cursor-pointer shrink-0">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={metricOn}
                        disabled={!catEnabled}
                        onChange={e => toggleMetric(metric.id, e.target.checked)}
                      />
                      <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-violet-500" />
                    </label>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Warnings */}
          {enabledCatCount === 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700 font-medium">
              All categories are disabled. Enable at least one to generate insights.
            </div>
          )}
          {catEnabled && enabledMetricCount === 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700 font-medium">
              All metrics in {activeCat.label} are disabled. Gemini will skip this category even though it's on.
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-slate-200 shrink-0">
          <button
            onClick={onClose}
            className="w-full bg-violet-600 hover:bg-violet-700 text-white font-bold py-2.5 rounded-xl transition-colors text-sm"
          >
            Done
          </button>
        </div>

      </div>
    </div>
  );
};

export default InsightStatSettings;
